// src/utils/resumeParser.ts – Comprehensive Resume Clarity Analysis
export interface ClarityIssue {
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  suggestion: string;
  highlights?: Array<{ text: string; reason: string }>; // For yellow highlighting
}

export interface MissingField {
  field: string;
  severity: 'critical' | 'high' | 'medium';
  description: string;
  impact: string;
}

export interface ClarityAnalysis {
  overallScore: number; // 0-100
  issueCount: number;
  missingFields: MissingField[];
  clarityIssues: ClarityIssue[];
  highlights: Array<{ text: string; reason: string; color: string }>; // For yellow highlights
  improvements: string[];
}

const ACTION_VERBS = [
  'achieved', 'coordinated', 'designed', 'developed', 'directed', 'executed',
  'formulated', 'founded', 'implemented', 'improved', 'increased', 'led',
  'managed', 'optimized', 'oversaw', 'pioneered', 'produced', 'programmed',
  'recognized', 'reduced', 'reorganized', 'resolved', 'spearheaded', 'streamlined',
  'strengthened', 'structured', 'supervised', 'transformed'
];

const STRONG_ACTION_PATTERN = new RegExp(`\\b(${ACTION_VERBS.join('|')})\\b`, 'gi');
const DATE_PATTERN = /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2}?,?\s+\d{4}|\d{4}[\s–-]+\d{4}|\d{4}[\s–-]+Present\b/gi;
const EMAIL_PATTERN = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
const PHONE_PATTERN = /\b(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})\b/;
const LINKEDIN_PATTERN = /linkedin\.com\/in\/|linkedin\.com\/company\//i;
const METRIC_PATTERN = /\b(increase|decrease|improve|reduce|grow|save|earn|generate|accelerate|enhance)(.{0,30}?)(\d+%|[$€£]\d+[KMB]?|\d+x|[0-9,]+\s+(users|customers|leads|clients|projects|tasks))/gi;
const WEAK_PHRASE_PATTERN = /\b(responsible for|involved in|helped|worked on|did|made|got)\b/gi;

export const analyzeResumeClarity = (resumeText: string): ClarityAnalysis => {
  const missingFields: MissingField[] = [];
  const clarityIssues: ClarityIssue[] = [];
  const highlights: Array<{ text: string; reason: string; color: string }> = [];
  const improvements: string[] = [];
  
  let score = 100;

  // ========== CONTACT INFORMATION CHECK ==========
  const hasEmail = EMAIL_PATTERN.test(resumeText);
  const hasPhone = PHONE_PATTERN.test(resumeText);
  const hasLinkedIn = LINKEDIN_PATTERN.test(resumeText);

  if (!hasEmail) {
    missingFields.push({
      field: 'Email Address',
      severity: 'critical',
      description: 'Email address is missing from resume',
      impact: 'ATS systems cannot contact you; recruiters cannot reach out'
    });
    clarityIssues.push({
      type: 'missing-email',
      severity: 'critical',
      description: 'Email address not found',
      suggestion: 'Add your professional email at the top of your resume'
    });
    score -= 20;
    improvements.push('Add your email address at the top for recruiters to contact you');
  }

  if (!hasPhone) {
    missingFields.push({
      field: 'Phone Number',
      severity: 'high',
      description: 'Phone number is missing',
      impact: 'Recruiters may prefer phone contact; limits accessibility'
    });
    clarityIssues.push({
      type: 'missing-phone',
      severity: 'high',
      description: 'Phone number not found',
      suggestion: 'Add your phone number in standard format (123) 456-7890'
    });
    score -= 10;
    improvements.push('Include your phone number so recruiters can call you directly');
  }

  if (!hasLinkedIn) {
    clarityIssues.push({
      type: 'missing-linkedin',
      severity: 'medium',
      description: 'LinkedIn profile URL not included',
      suggestion: 'Add your LinkedIn profile URL (https://linkedin.com/in/yourprofile)'
    });
    score -= 5;
    improvements.push('Add your LinkedIn profile URL to build credibility');
  }

  // ========== SECTION PRESENCE CHECK ==========
  const hasExperience = /\b(experience|work history|employment|professional|position|role)\b/i.test(resumeText);
  const hasEducation = /\b(education|degree|university|college|bachelor|master|diploma|certification)\b/i.test(resumeText);
  const hasSkills = /\b(skills|technical|proficiencies|competencies|expertise)\b/i.test(resumeText);

  if (!hasExperience) {
    missingFields.push({
      field: 'Experience Section',
      severity: 'critical',
      description: 'No work experience section detected',
      impact: 'ATS cannot parse work history; critical for screening'
    });
    score -= 25;
    improvements.push('Create a dedicated "Experience" section with your job history');
  }

  if (!hasEducation) {
    missingFields.push({
      field: 'Education Section',
      severity: 'high',
      description: 'No education section detected',
      impact: 'Many roles require education verification; missing signal'
    });
    score -= 15;
    improvements.push('Add "Education" section with degree, school, and graduation date');
  }

  if (!hasSkills) {
    missingFields.push({
      field: 'Skills Section',
      severity: 'high',
      description: 'No dedicated skills section',
      impact: 'ATS keywords may be missed; harder to match job requirements'
    });
    score -= 15;
    improvements.push('Create a "Skills" section listing technical and professional abilities');
  }

  // ========== DATE FORMATTING CHECK ==========
  const dates = resumeText.match(DATE_PATTERN) || [];
  if (dates.length === 0) {
    clarityIssues.push({
      type: 'missing-dates',
      severity: 'high',
      description: 'Employment dates not clearly formatted',
      suggestion: 'Use standard date format: "Jan 2020 – Present" or "2020 – 2023"',
      highlights: [{ text: 'employment date', reason: 'Missing or unclear date formatting' }]
    });
    score -= 12;
    improvements.push('Add clear employment dates (e.g., "Jan 2020 – Dec 2023")');
  } else if (dates.length < 2 && hasExperience) {
    clarityIssues.push({
      type: 'insufficient-dates',
      severity: 'medium',
      description: 'Some positions missing dates',
      suggestion: 'Ensure every role includes start and end dates'
    });
    score -= 8;
    improvements.push('Include start and end dates for each position');
  }

  // ========== ACTION VERB ANALYSIS ==========
  const actionVerbMatches = resumeText.match(STRONG_ACTION_PATTERN) || [];
  if (actionVerbMatches.length === 0) {
    clarityIssues.push({
      type: 'no-action-verbs',
      severity: 'high',
      description: 'No strong action verbs detected',
      suggestion: 'Start bullet points with: Achieved, Designed, Implemented, Led, Managed, Improved'
    });
    score -= 15;
    improvements.push('Use strong action verbs (Led, Designed, Implemented, Improved, etc.)');
  } else if (actionVerbMatches.length < 5) {
    clarityIssues.push({
      type: 'weak-action-verbs',
      severity: 'medium',
      description: `Only ${actionVerbMatches.length} strong action verbs found`,
      suggestion: 'Increase action verbs in bullet points to show impact and leadership'
    });
    score -= 10;
    improvements.push(`Increase action verbs throughout your resume (currently: ${actionVerbMatches.length})`);
  }

  // ========== WEAK PHRASE DETECTION ==========
  const weakPhrases = resumeText.match(WEAK_PHRASE_PATTERN) || [];
  if (weakPhrases.length > 0) {
    clarityIssues.push({
      type: 'weak-phrases',
      severity: 'medium',
      description: `Found ${weakPhrases.length} passive/weak phrases`,
      suggestion: 'Replace passive language: "Responsible for" → "Led", "Involved in" → "Implemented"',
      highlights: weakPhrases.slice(0, 5).map(phrase => ({ text: phrase, reason: 'Weak/passive phrase' }))
    });
    score -= 10;
    improvements.push(`Replace passive phrases (found: ${weakPhrases.length}) with active, strong verbs`);
  }

  // ========== QUANTIFIABLE METRICS CHECK ==========
  const metricMatches = resumeText.match(METRIC_PATTERN) || [];
  if (metricMatches.length < 3) {
    clarityIssues.push({
      type: 'missing-metrics',
      severity: 'high',
      description: `Only ${metricMatches.length} quantifiable achievements found`,
      suggestion: 'Add numbers/percentages: "Increased sales by 40%", "Managed $5M budget", "Led 8-person team"'
    });
    score -= 15;
    improvements.push(`Quantify your achievements with metrics (currently: ${metricMatches.length})`);
    highlights.push(...metricMatches.map(m => ({ text: m, reason: 'Good use of metrics!', color: 'yellow' })));
  } else {
    improvements.push(`Good use of quantifiable metrics (${metricMatches.length} found)`);
  }

  // ========== BULLET POINT QUALITY CHECK ==========
  const bulletPoints = resumeText.split('\n').filter(line => /^[\s]*[-•]\s/.test(line));
  const weakBullets = bulletPoints.filter(bullet => {
    const cleaned = bullet.replace(/^[\s]*[-•]\s/, '').trim();
    return cleaned.length < 15 || (cleaned.length > 150 && !METRIC_PATTERN.test(cleaned));
  });

  if (weakBullets.length > 0) {
    clarityIssues.push({
      type: 'weak-bullets',
      severity: 'medium',
      description: `${weakBullets.length} bullet points are too short or too long`,
      suggestion: 'Keep bullets concise (15-120 chars) and impactful with results'
    });
    score -= 8;
    improvements.push(`Optimize ${weakBullets.length} bullet points for clarity and impact`);
  }

  // ========== KEYWORD DENSITY CHECK ==========
  // Industry-neutral: checks for ANY specific skill/tool/credential nouns rather
  // than a hardcoded tech list. A "keyword" here is any capitalised word that
  // appears more than once — a simple but field-agnostic proxy for specialised
  // vocabulary (Python, HIPAA, CPA, AutoCAD, Salesforce, etc. all qualify).
  const capitalWords = (resumeText.match(/\b[A-Z][a-zA-Z0-9.#+/-]{2,}\b/g) || []);
  const capitalWordFreq = new Map<string, number>();
  for (const w of capitalWords) {
    capitalWordFreq.set(w, (capitalWordFreq.get(w) || 0) + 1);
  }
  const repeatedSpecificTerms = Array.from(capitalWordFreq.values()).filter(n => n > 1).length;

  if (repeatedSpecificTerms < 3) {
    clarityIssues.push({
      type: 'low-keywords',
      severity: 'medium',
      description: 'Few specific skills or tools named in the resume',
      suggestion: 'Name the tools, systems, certifications, or technologies you use — ATS scanners look for them'
    });
    score -= 8;
    improvements.push('Mention specific skills, tools, or credentials relevant to your field');
  }

  // ========== LENGTH CHECK ==========
  const wordCount = resumeText.split(/\s+/).length;
  if (wordCount < 200) {
    clarityIssues.push({
      type: 'too-short',
      severity: 'medium',
      description: `Resume is only ${wordCount} words (recommended: 300-600)`,
      suggestion: 'Expand with more details, achievements, and quantifiable results'
    });
    score -= 10;
    improvements.push(`Expand your resume (currently ${wordCount} words, target: 300-600)`);
  } else if (wordCount > 800) {
    clarityIssues.push({
      type: 'too-long',
      severity: 'medium',
      description: `Resume is ${wordCount} words (recommended: 300-600)`,
      suggestion: 'Trim excessive details; focus on most relevant achievements'
    });
    score -= 8;
    improvements.push(`Trim your resume (currently ${wordCount} words, target: 300-600)`);
  }

  // Ensure score is between 0-100
  score = Math.max(0, Math.min(100, score));

  return {
    overallScore: score,
    issueCount: clarityIssues.length + missingFields.length,
    missingFields,
    clarityIssues,
    highlights,
    improvements
  };
};